import React from "react";
import "./StepSkeleton.scss";

// Purely presentational skeleton shown as the Suspense fallback while a gift
// step's lazy chunk loads. Each variant loosely mirrors that step's real layout
// (form fields, audio recorder, card grid, review rows, payment + summary) so
// the transition reads as the page taking shape rather than a blank spinner.
// This renders NOTHING interactive and holds no state — it never touches the
// real step logic.

type Block = { className?: string; style?: React.CSSProperties };

const Line: React.FC<Block> = ({ className = "", style }) => (
  <span className={`sk sk-line ${className}`} style={style} />
);

const Box: React.FC<Block> = ({ className = "", style }) => (
  <span className={`sk sk-box ${className}`} style={style} />
);

const Header: React.FC = () => (
  <div className="sk-header">
    <Line className="sk-title" />
    <Line className="sk-subtitle" />
  </div>
);

const FieldSkeleton: React.FC = () => (
  <div className="sk-field">
    <Line className="sk-label" />
    <Box className="sk-input" />
  </div>
);

const MessageSkeleton: React.FC = () => (
  <>
    <Header />
    {/* Audio recorder block */}
    <div className="sk-panel">
      <Line className="sk-label sk-w-40" />
      <div className="sk-recorder">
        <Box className="sk-record-btn" />
        <div className="sk-waveform">
          {Array.from({ length: 28 }).map((_, i) => (
            <span key={i} className="sk sk-wave-bar" />
          ))}
        </div>
      </div>
    </div>
    {/* Message text */}
    <div className="sk-field">
      <Line className="sk-label" />
      <Box className="sk-textarea" />
    </div>
    <Box className="sk-btn" />
  </>
);

const GiftSkeleton: React.FC = () => (
  <>
    <Header />
    <Line className="sk-label sk-w-30" />
    {/* Select gift type radio row */}
    <div className="sk-radio-row">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="sk-radio">
          <span className="sk sk-radio-dot" />
          <Line className="sk-w-60" />
        </div>
      ))}
    </div>
    {/* Recipient / occasion / date info box */}
    <div className="sk-info-box">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="sk-info-col">
          <Line className="sk-w-50 sk-sm" />
          <Line className="sk-w-80" />
        </div>
      ))}
    </div>
    {/* Card grid */}
    <div className="sk-card-grid">
      {Array.from({ length: 4 }).map((_, i) => (
        <Box key={i} className="sk-card" />
      ))}
    </div>
  </>
);

const ReviewSkeleton: React.FC = () => (
  <>
    <Header />
    {Array.from({ length: 3 }).map((_, i) => (
      <div key={i} className="sk-review-block">
        <Line className="sk-label sk-w-30" />
        <div className="sk-review-rows">
          <Line className="sk-w-90" />
          <Line className="sk-w-70" />
          <Line className="sk-w-80" />
        </div>
      </div>
    ))}
    <Box className="sk-btn" />
  </>
);

const DeliverySkeleton: React.FC = () => (
  <>
    <Header />
    <div className="sk-two-col">
      <div className="sk-col-main">
        <Box className="sk-wallet-btn" />
        <div className="sk-panel">
          <Line className="sk-label sk-w-40" />
          <FieldSkeleton />
          <FieldSkeleton />
          <div className="sk-field-row">
            <FieldSkeleton />
            <FieldSkeleton />
          </div>
          <Box className="sk-btn" />
        </div>
      </div>
      <div className="sk-col-side">
        <Box className="sk-summary" />
      </div>
    </div>
  </>
);

const VARIANTS: { [key: string]: React.FC } = {
  "3": MessageSkeleton,
  "4": GiftSkeleton,
  "5": ReviewSkeleton,
  "6": DeliverySkeleton,
};

const StepSkeleton: React.FC<{ step: string }> = ({ step }) => {
  const Variant = VARIANTS[step] || GiftSkeleton;
  return (
    <div className="step-skeleton step-form" aria-busy="true" aria-live="polite">
      <span className="sr-only">Loading step…</span>
      <Variant />
    </div>
  );
};

export default StepSkeleton;
